import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Boxes, Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left visual */}
      <div className="relative hidden lg:block overflow-hidden border-r border-border">
        <img
          src="https://images.unsplash.com/photo-1637946175559-22c4fe13fc54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsYWNrJTIwZ2VvbWV0cmljfGVufDB8fHx8MTc4OTc0MzMxM3ww&ixlib=rb-4.1.0&q=85"
          alt="abstract"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 grain" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12 max-w-md">
          <h2 className="text-4xl font-semibold tracking-tight leading-tight">
            Verifică orice AWB Cargus în câteva secunde.
          </h2>
          <p className="mt-4 text-muted-foreground font-mono text-sm">
            Livrat · Nelivrat · Plătit · Neplătit — direct din API-ul curierului.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="h-9 w-9 rounded-sm bg-primary flex items-center justify-center">
              <Boxes size={20} className="text-primary-foreground" />
            </div>
            <div className="text-lg font-semibold tracking-tight">AWB Verifier</div>
          </div>

          <h1 className="text-2xl font-semibold tracking-tight">
            {mode === "login" ? "Autentificare" : "Cont nou"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">
            {mode === "login" ? "Intră în contul tău pentru a verifica AWB-uri." : "Creează un cont pentru a începe."}
          </p>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <input
                data-testid="name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nume"
                className="w-full bg-card border border-border rounded-sm px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors"
              />
            )}
            <input
              data-testid="email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full bg-card border border-border rounded-sm px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors"
            />
            <input
              data-testid="password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Parolă"
              className="w-full bg-card border border-border rounded-sm px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors"
            />

            {error && (
              <div data-testid="auth-error" className="text-xs text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="submit-btn"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-sm px-3 py-2.5 text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              {mode === "login" ? "Intră în cont" : "Creează cont"}
            </button>
          </form>

          <button
            data-testid="toggle-mode"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError("");
            }}
            className="mt-5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {mode === "login" ? "Nu ai cont? Înregistrează-te" : "Ai deja cont? Autentifică-te"}
          </button>
        </div>
      </div>
    </div>
  );
}
